/**
 * AgentRunFindingsCard —— doc-review 技能步摘要的结构化呈现。
 *
 * 后端 doc-review 技能把整份审查结论（summary / verdict / overall_level）与
 * 逐条风险（findings[]，snake_case 原样）打包成 JSON 写入步骤的
 * output_summary。此前详情页把这段 JSON 当纯文本展示，律师无法直接阅读。
 *
 * 解析是防御式的：JSON 解析失败、或解析结果不含 findings 数组时，回退为
 * 纯文本展示（与既有摘要样式一致），不报错不空白。findings 为空且 verdict
 * 非 reviewable（too_crude / not_a_contract 等）时只呈现结论区、不出卡片
 * 列表——此时 summary 即结论本身。
 */

import { Button, Tag, Typography, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";

const { Paragraph, Text } = Typography;

/** 风险等级徽标（任务约定呈现：high 红 / medium 橙 / low 蓝，中文「高/中/低」）。 */
const SEVERITY_TAG_COLOR: Record<string, string> = {
  high: "red",
  medium: "orange",
  low: "blue",
};

const SEVERITY_LABEL: Record<string, string> = {
  high: "高",
  medium: "中",
  low: "低",
};

/** 审查结论徽标（措辞与 workbench ReviewCardList 的 VERDICT_LABEL 保持一致）。 */
const VERDICT_LABEL: Record<string, string> = {
  reviewable: "可逐条审查",
  too_crude: "内容过于简陋",
  not_a_contract: "不构成合同",
  review_failed: "审查未完成",
  review_running: "审查进行中",
};

/** findings[] 单条的防御式形状（以后端 doc-review 真实产出为准）。 */
interface ReviewFinding {
  title?: string;
  category?: string;
  severity?: string;
  suggestion?: string;
  clause_text?: string;
  legal_basis?: string;
  source_location?: string;
  /** 可选键：可直接替换的完整条款文本。 */
  suggested_clause_text?: string;
}

interface ReviewSummary {
  summary?: string;
  verdict?: string;
  overall_level?: string;
  findings?: ReviewFinding[];
}

/**
 * 摘要文本 → 结构化审查结论。仅当解析成功且含 findings 数组时返回对象；
 * 其余（非法 JSON / 非对象 / 无 findings 数组）一律返回 null 走纯文本回退。
 */
function parseReviewSummary(text: string): ReviewSummary | null {
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== "object" || parsed === null) return null;
    if (!Array.isArray((parsed as ReviewSummary).findings)) return null;
    return parsed as ReviewSummary;
  } catch {
    return null;
  }
}

/** 仅接受非空字符串，其余（undefined / 空串 / 非字符串）归一为 null。 */
function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function SeverityTag({ severity }: { severity: string }) {
  return (
    <Tag color={SEVERITY_TAG_COLOR[severity] ?? "default"} data-testid="finding-severity">
      {SEVERITY_LABEL[severity] ?? severity}
    </Tag>
  );
}

/** 字段小标签（修改建议 / 法律依据等行首引导）。 */
function fieldLabel(text: string) {
  return (
    <Text strong style={{ fontSize: "var(--jerry-font-size-xs)", color: "var(--jerry-text-tertiary)", marginRight: "var(--jerry-sp-2)" }}>
      {text}
    </Text>
  );
}

function FindingCard({ finding }: { finding: ReviewFinding }) {
  const [messageApi, contextHolder] = message.useMessage();
  const severity = str(finding.severity);
  const title = str(finding.title);
  const category = str(finding.category);
  const sourceLocation = str(finding.source_location);
  const clauseText = str(finding.clause_text);
  const suggestion = str(finding.suggestion);
  const legalBasis = str(finding.legal_basis);
  const suggestedClauseText = str(finding.suggested_clause_text);

  const handleCopy = async () => {
    if (!suggestedClauseText) return;
    try {
      await navigator.clipboard.writeText(suggestedClauseText);
      messageApi.success("已复制建议替换条款");
    } catch {
      messageApi.error("复制失败，请手动选择文本复制");
    }
  };

  return (
    <div
      data-testid="finding-card"
      style={{
        padding: "var(--jerry-sp-3)",
        border: "1px solid var(--jerry-border-default)",
        borderRadius: 8,
        background: "var(--jerry-gray-50)",
      }}
    >
      {contextHolder}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--jerry-sp-2)",
          flexWrap: "wrap",
          marginBottom: clauseText || suggestion ? "var(--jerry-sp-2)" : 0,
        }}
      >
        {severity && <SeverityTag severity={severity} />}
        {title && (
          <Text strong style={{ color: "var(--jerry-text-primary)" }}>
            {title}
          </Text>
        )}
        {category && <Text type="secondary">{category}</Text>}
        {sourceLocation && <Text type="secondary">位置：{sourceLocation}</Text>}
      </div>

      {clauseText && (
        <blockquote
          data-testid="finding-clause-text"
          style={{
            margin: "0 0 var(--jerry-sp-2)",
            padding: "var(--jerry-sp-2) var(--jerry-sp-3)",
            borderLeft: "3px solid var(--jerry-primary-500)",
            background: "var(--jerry-gray-100)",
            borderRadius: 4,
            color: "var(--jerry-text-secondary)",
            fontSize: "var(--jerry-font-size-sm)",
            whiteSpace: "pre-wrap",
          }}
        >
          {clauseText}
        </blockquote>
      )}

      {suggestion && (
        <Paragraph style={{ marginBottom: "var(--jerry-sp-1)", color: "var(--jerry-text-primary)" }}>
          {fieldLabel("修改建议")}
          {suggestion}
        </Paragraph>
      )}

      {legalBasis && (
        <Paragraph style={{ marginBottom: suggestedClauseText ? "var(--jerry-sp-2)" : 0, color: "var(--jerry-text-secondary)" }}>
          {fieldLabel("法律依据")}
          {legalBasis}
        </Paragraph>
      )}

      {suggestedClauseText && (
        <div
          data-testid="finding-suggested-clause"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "var(--jerry-sp-2)",
            padding: "var(--jerry-sp-2)",
            border: "1px dashed var(--jerry-border-default)",
            borderRadius: 4,
            background: "var(--jerry-primary-subtle)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {fieldLabel("建议替换条款")}
            <Text style={{ color: "var(--jerry-text-primary)", whiteSpace: "pre-wrap", fontSize: "var(--jerry-font-size-sm)" }}>
              {suggestedClauseText}
            </Text>
          </div>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={handleCopy}
            data-testid="finding-copy-button"
          >
            复制
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AgentRunFindingsCard({ summary }: { summary: string | null }) {
  const parsed = summary != null ? parseReviewSummary(summary) : null;

  // 回退：不可结构化时保持既有纯文本摘要样式。
  if (!parsed) {
    return (
      <Text type="secondary" style={{ whiteSpace: "pre-wrap", fontSize: "var(--jerry-font-size-sm)" }}>
        {summary}
      </Text>
    );
  }

  const findings = parsed.findings ?? [];
  const overallLevel = str(parsed.overall_level);
  const verdict = str(parsed.verdict);

  return (
    <div data-testid="agent-run-findings-card" style={{ marginTop: "var(--jerry-sp-2)" }}>
      <div
        data-testid="agent-run-findings-header"
        style={{ display: "flex", alignItems: "center", gap: "var(--jerry-sp-2)", flexWrap: "wrap", marginBottom: "var(--jerry-sp-2)" }}
      >
        {verdict && (
          <Tag data-testid="agent-run-findings-verdict">{VERDICT_LABEL[verdict] ?? verdict}</Tag>
        )}
        {overallLevel && (
          <Tag color={SEVERITY_TAG_COLOR[overallLevel] ?? "default"} data-testid="agent-run-findings-level">
            整体风险：{SEVERITY_LABEL[overallLevel] ?? overallLevel}
          </Tag>
        )}
      </div>
      {str(parsed.summary) && (
        <Paragraph data-testid="agent-run-findings-summary" style={{ marginBottom: "var(--jerry-sp-3)", color: "var(--jerry-text-primary)" }}>
          {parsed.summary}
        </Paragraph>
      )}

      {findings.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--jerry-sp-3)" }}>
          {findings.map((finding, idx) => (
            <FindingCard key={idx} finding={finding} />
          ))}
        </div>
      )}
    </div>
  );
}
